import { useEffect, useState } from 'react';

import { DialogTitle, DialogContent, DialogActions, Box } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';

import { useQuery } from '@apollo/client/react';

import { graphql } from '@/gql';

import { Or2Button } from '@/components/button/Or2Button';

import { CreateFolderDialog } from '../file-manager/dialogs/CreateFolderDialog';

import type { File } from './FileMenuItem';
import { FilePickerView } from './FilePickerView';

export interface RootOwner {
  _id: string;
  type: 'Group' | 'Service';
}

export interface FilePickerProps {
  rootOwner: RootOwner;
  parentId: string | null | undefined;
  title: string;
  fileActionName: string;
  parentActionName: string;
  selectionMode: 'file' | 'folder' | 'both';
  preventRootSelection?: boolean;
  onChangeParent: (parentId: string | null | undefined) => void;
  onClose: () => void;
  onSelect: (
    fileId: string | null | undefined,
    ownerId: string,
    file: File | null | undefined,
  ) => void;
  disableItemWithReason?: (file: File) => null | undefined | string;
  displayCreateFolderButton?: boolean;
  onBack?: () => void;
}

interface SelectedFile {
  fileId: string;
  ownerId: string;
  file: File | null | undefined;
}

export const FILE_PICKER_QUERY = graphql(`
  query FilePicker(
    $ownerId: ID!
    $fileId: ID
    $includeSharingGroups: Boolean!
  ) {
    groupsForSharing(item: { id: $ownerId, type: "Service" })
      @include(if: $includeSharingGroups) {
      edges {
        node {
          _id
          name
        }
      }
    }

    filesForOwner(ownerId: $ownerId, parentId: $fileId, includeChildren: true) {
      owner {
        ... on Service {
          _id
          name
          editableByUser(feature: "files")
        }
        ... on Group {
          _id
          name
          referenceName
          wholeGroupGeneralName
          type {
            _id
            showFilesForChildrenWithTypesConnection {
              edges {
                node {
                  _id
                }
              }
            }
          }
          userCanEditFiles
          userCanLockFiles
        }
      }

      relatedOwners {
        owner {
          ... on Group {
            _id
          }

          ... on Service {
            _id
          }
        }
        userCanEditFiles
        userCanLockFiles
        userCanPreventDownloadOfFiles
      }

      parent {
        ...fileFields
        _id
        _exists
        filename
        folder
        owner {
          ... on Group {
            _id
            name
          }

          ... on Service {
            _id
            name
          }
        }
        breadcrumbs {
          file {
            ...fileFields
          }
        }
      }

      children {
        _id
        name
        userCanEditFiles
        userCanLockFiles
      }

      filesConnection {
        edges {
          node {
            ...fileFields
          }
        }
      }
    }
  }
`);

export const FilePicker = ({
  parentId,
  fileActionName,
  parentActionName,
  onClose,
  rootOwner,
  selectionMode,
  preventRootSelection = false,
  disableItemWithReason,
  onChangeParent,
  onSelect,
  title,
  displayCreateFolderButton = true,
  onBack,
}: FilePickerProps) => {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const { data, loading } = useQuery(FILE_PICKER_QUERY, {
    variables: {
      ownerId: rootOwner._id,
      fileId: parentId || null,
      includeSharingGroups: false,
    },
  });

  const rootOwnerId = rootOwner?._id;

  const files =
    data?.filesForOwner?.filesConnection?.edges?.map((edge) => edge.node) ?? [];

  const owner = data?.filesForOwner?.owner ?? null;
  const ownerName = owner?.name ?? '';

  const parent = data?.filesForOwner?.parent ?? null;
  const parentName = parent?.filename ?? ownerName;

  const [selectedFile, setSelectedFile] = useState<
    SelectedFile | null | undefined
  >(null);

  useEffect(() => {
    setSelectedFile(null);
  }, [parentId, rootOwnerId]);

  const handleSelectedFileChange = (file: File | null | undefined) => {
    const ownerId = file?.owner?._id;
    const fileId = file?._id;

    const allowSelection =
      selectionMode === 'both' ||
      (selectionMode === 'file' && !file?.folder) ||
      (selectionMode === 'folder' && file?.folder);

    const selectedFile =
      fileId && ownerId && allowSelection
        ? {
            fileId,
            ownerId,
            file,
          }
        : null;

    setSelectedFile(selectedFile);
  };
  const createFolder = () => {
    setCreateDialogOpen(true);
  };

  const handleSelect = () => {
    if (selectedFile) {
      onSelect(selectedFile.fileId, selectedFile.ownerId, selectedFile.file);
      return;
    }

    if (preventRootSelection) {
      onSelect(null, rootOwner._id, null);
      return;
    }

    onSelect(parent?._id, parent?.owner?._id || rootOwner._id, parent);
  };

  return (
    <>
      <DialogTitle id="folder-selector-dialog-title">{title}</DialogTitle>
      {onBack && (
        <Box sx={{ px: 3, pb: 1 }}>
          <Or2Button
            size="small"
            color="secondary"
            startIcon={<ArrowBack />}
            onClick={onBack}
          >
            Back to select groups
          </Or2Button>
        </Box>
      )}
      <DialogContent style={{ paddingTop: 0 }}>
        <FilePickerView
          loading={loading}
          files={files}
          disableItemWithReason={disableItemWithReason}
          selected={selectedFile && selectedFile.fileId}
          onChange={handleSelectedFileChange}
          onChangeParent={onChangeParent}
          rootOwnerName={ownerName}
          rootOwnerId={rootOwner._id}
          parent={parent}
        />
      </DialogContent>
      <DialogActions>
        {displayCreateFolderButton && (
          <div style={{ flexGrow: 1 }}>
            <Or2Button color="secondary" onClick={createFolder}>
              Create folder
            </Or2Button>
          </div>
        )}
        <Or2Button color="secondary" onClick={onClose}>
          Cancel
        </Or2Button>
        <Or2Button
          variant="contained"
          disableElevation
          disabled={
            !preventRootSelection || selectedFile?.file
              ? disableItemWithReason
                ? Boolean(
                    selectedFile?.file
                      ? disableItemWithReason(selectedFile.file)
                      : false,
                  )
                : false
              : true
          }
          onClick={handleSelect}
        >
          {selectedFile ? fileActionName : parentActionName}
        </Or2Button>
      </DialogActions>
      <CreateFolderDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        owner={rootOwnerId}
        rootOwnerId={rootOwnerId}
        parentId={parentId}
        parentName={parentName}
        rootOwner={rootOwner}
      />
    </>
  );
};
